import { boundedText } from "./bounded-text.js";
import { nameKey } from "./catalog-import-text.js";
import { DomainValidationError } from "./identifiers.js";
import { ianaTimeZone } from "./time-zone.js";
// Location and time-zone rules of itinerary planning (CHT-743; contract
// `docs/architecture/milestone-d-contracts.md`, sections 4.2 and 4.5). Pure: no I/O, no clock, no provider.
//
// STORAGE POLICY. The owner's Places clearance (`docs/engineering/places.md`, "Conditions of the clearance") lets
// Lafayette keep a Place ID and call metadata only. A name, an address, a coordinate or a time zone that Google
// reports is transient. So nothing in this file produces a value that is stored from Google except the Place ID,
// the field set code, the observation time, the decision and the count of candidates. A zone from Places is a
// SUGGESTION for one run: it becomes an item's zone only when the customer or staff confirm it, and it is then
// stored with the source `CUSTOMER` or `STAFF`.
/** The code of the Places planning field set (one Place Details call: name, point, zone, address parts). */
export const PLACE_PLANNING_FIELD_SET = "PLACE_PLANNING_V1";
/**
 * The billing tier of the planning field set. Place Details with `displayName` and `timeZone` is Pro; the other
 * fields of the mask are Essentials or IDs Only (read 2026-10-17, `docs/engineering/places.md`).
 */
export const PLACE_PLANNING_SKU_TIER = "PRO";
/** The most candidates the planning search shows. The ambiguity rule reads exactly these. */
export const PLANNING_CANDIDATES_MAX = 5;
/**
 * Where the location of an item comes from, in priority order: the first entry that an item has wins.
 * `CATALOG` is Lafayette's own geography of a catalog item, `STAFF` is a point that staff typed from a non-Google
 * source, `PLACES_REFERENCE` is a Place ID (no Lafayette-owned point at all).
 */
export const locationSources = Object.freeze(["CATALOG", "STAFF", "PLACES_REFERENCE"]);
/** The outcome of resolving the text of a place. */
export const locationDecisions = Object.freeze(["RESOLVED", "AMBIGUOUS", "UNRESOLVED"]);
/** Who confirmed a decision: nobody (a search outcome), the customer, or a staff member. */
export const locationConfirmerKinds = Object.freeze(["NONE", "CUSTOMER", "STAFF"]);
/** The sources an item's zone may be stored with. A zone reported by Places is never a source. */
export const ZONE_CONFIRMATION_SOURCES = Object.freeze(["CATALOG", "STAFF", "CUSTOMER"]);
/** The field sets a location evidence row may name. */
export const locationEvidenceFieldSets = Object.freeze([
    "SEARCH_CANDIDATES_V1",
    PLACE_PLANNING_FIELD_SET,
]);
/** The one provider of Place IDs. */
export const LOCATION_EVIDENCE_PROVIDER = "google_places";
const PLACE_ID_MAX = 255;
const fail = (field, rule) => new DomainValidationError(field, rule);
/**
 * The strongest source an item has, or `null` when it has none (the location is then unresolved). A catalog item
 * is always `CATALOG`, even when its catalog geography is incomplete: the catalog is where staff fix it.
 */
export function pickLocationSource(facts) {
    if (facts.catalogItem)
        return "CATALOG";
    if (facts.ownedPoint)
        return "STAFF";
    if (facts.placeId)
        return "PLACES_REFERENCE";
    return null;
}
/** The zone of a catalog item, or `null` when the catalog has none (the customer or staff must confirm one). */
export function catalogItemZone(catalog) {
    if (catalog.timeZone === null)
        return null;
    try {
        return ianaTimeZone(catalog.timeZone);
    }
    catch {
        return null;
    }
}
/**
 * The decision for the candidates of one search. None is `UNRESOLVED`. One is `RESOLVED`. Two or more are
 * `RESOLVED` only when every candidate names the same country and the same locality; a difference, or a candidate
 * whose locality or country is unknown, makes the search `AMBIGUOUS` (Lafayette does not guess which "Orchard" the
 * customer means). Localities compare by `nameKey` (case, accents and spacing do not matter).
 */
export function decidePlaceResolution(candidates) {
    if (candidates.length === 0)
        return "UNRESOLVED";
    if (candidates.length === 1)
        return "RESOLVED";
    const first = candidates[0];
    const country = first.countryCode?.toUpperCase() ?? null;
    const locality = first.locality === null ? "" : nameKey(first.locality);
    if (country === null || locality === "")
        return "AMBIGUOUS";
    for (const other of candidates.slice(1)) {
        const otherLocality = other.locality === null ? "" : nameKey(other.locality);
        if (other.countryCode?.toUpperCase() !== country || otherLocality !== locality) {
            return "AMBIGUOUS";
        }
    }
    return "RESOLVED";
}
// --- zone comparison ------------------------------------------------------------------------------------
const offsetFormatters = new Map();
function offsetMinutesAt(instant, zone) {
    let formatter = offsetFormatters.get(zone);
    if (formatter === undefined) {
        formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: zone,
            calendar: "gregory",
            numberingSystem: "latn",
            hourCycle: "h23",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        offsetFormatters.set(zone, formatter);
    }
    const whole = Math.floor(instant.getTime() / 1000) * 1000;
    const parts = formatter.formatToParts(new Date(whole));
    const part = (type) => Number(parts.find((entry) => entry.type === type)?.value ?? "0");
    const asUtc = Date.UTC(part("year"), part("month") - 1, part("day"), part("hour"), part("minute"), part("second"));
    return Math.round((asUtc - whole) / 60_000);
}
const PROBE_DAYS = 30;
const PROBE_COUNT = 13;
/**
 * Whether two zone names follow the same offset rules around `at`: the same UTC offset at `at` and at twelve probes
 * 30 days apart (a year in all). `Asia/Calcutta` and `Asia/Kolkata` follow the same rules; `Asia/Kolkata` and
 * `Asia/Dhaka` do not. Two zones with the same offsets all year agree on every wall time, which is all a plan
 * needs, so they compare equal even when their names differ.
 */
export function zonesShareRules(a, b, at) {
    if (a === b)
        return true;
    for (let index = 0; index < PROBE_COUNT; index += 1) {
        const probe = new Date(at.getTime() + index * PROBE_DAYS * 86_400_000);
        if (offsetMinutesAt(probe, a) !== offsetMinutesAt(probe, b))
            return false;
    }
    return true;
}
/**
 * `ITEM_ZONE_LOCATION_MISMATCH` rule: the item's zone does not follow the rules of the zone that the resolved place
 * is in. An unknown place zone is not a mismatch (nothing can be judged). `at` is the item's start instant, else
 * the moment of the check.
 */
export function itemZoneMismatchesPlace(itemZone, placeZone, at) {
    if (itemZone === null || placeZone === null)
        return false;
    return !zonesShareRules(itemZone, placeZone, at);
}
/**
 * What a confirmation of the item's zone does. The zone must be IANA text that the runtime knows (the caller names
 * it: the customer or staff chose it, perhaps from a transient suggestion). The source is the actor, never
 * Places. A catalog item takes the zone of its catalog item with the source `CATALOG` (`catalogZone`, from the
 * catalog row; a different `zone` is refused, and a catalog item with no zone is `CATALOG_ZONE_UNKNOWN`: staff fix
 * the catalog). A scheduled item already has an instant that was computed in its zone: a different zone would
 * change what that instant means, so it is refused here and goes through an edit that converts the wall time
 * (lane I1).
 */
export function planItemZoneConfirmation(input) {
    const catalogItem = input.item.kind === "CATALOG_ITEM";
    let zone;
    let source = input.actor;
    if (catalogItem) {
        if (input.catalogZone === null)
            return { ok: false, code: "CATALOG_ZONE_UNKNOWN" };
        if (input.zone !== null && input.zone !== input.catalogZone) {
            return { ok: false, code: "ZONE_NOT_FOR_CATALOG_ITEM" };
        }
        zone = input.catalogZone;
        source = "CATALOG";
    }
    else {
        zone = ianaTimeZone(input.zone, "timeZone");
    }
    const current = input.item.timeZone;
    if (current === null)
        return { ok: true, zone, source, change: "SET" };
    if (current === zone)
        return { ok: true, zone, source, change: "RECONFIRMED" };
    if (input.item.timingKind === "SCHEDULED")
        return { ok: false, code: "ZONE_CHANGE_NEEDS_EDIT" };
    return { ok: true, zone, source, change: "CHANGED" };
}
/**
 * Validates and normalizes a row. `RESOLVED` needs a Place ID and a confirmer (the actor of the attach); the
 * other two decisions carry no Place ID and no confirmer. `UNRESOLVED` has no candidate, `AMBIGUOUS` has at least
 * two, `RESOLVED` at least one, and none has more than five.
 */
export function validateLocationEvidenceDraft(input) {
    if (input.provider !== LOCATION_EVIDENCE_PROVIDER)
        throw fail("provider", "must be a known provider");
    if (!locationEvidenceFieldSets.includes(input.fieldSet)) {
        throw fail("fieldSet", "must be a known field set");
    }
    if (!locationDecisions.includes(input.decision)) {
        throw fail("decision", "must be a known decision");
    }
    if (!(input.observedAt instanceof Date) || Number.isNaN(input.observedAt.getTime())) {
        throw fail("observedAt", "must be a valid date");
    }
    const count = input.candidateCount;
    if (!Number.isInteger(count) || count < 0 || count > PLANNING_CANDIDATES_MAX) {
        throw fail("candidateCount", `must be a whole number from 0 to ${PLANNING_CANDIDATES_MAX}`);
    }
    if (input.decision === "UNRESOLVED" && count !== 0)
        throw fail("candidateCount", "must be 0");
    if (input.decision === "AMBIGUOUS" && count < 2)
        throw fail("candidateCount", "must be at least 2");
    if (input.decision === "RESOLVED" && count < 1)
        throw fail("candidateCount", "must be at least 1");
    if (input.decision === "RESOLVED") {
        if (input.placeId === null)
            throw fail("placeId", "is required for a resolved decision");
        if (input.confirmedBy.kind === "NONE")
            throw fail("confirmedBy", "is required for a resolved decision");
    }
    else {
        if (input.placeId !== null)
            throw fail("placeId", "is only for a resolved decision");
        if (input.confirmedBy.kind !== "NONE")
            throw fail("confirmedBy", "is only for a resolved decision");
    }
    return Object.freeze({
        ...input,
        placeId: input.placeId === null ? null : boundedText("placeId", input.placeId, PLACE_ID_MAX),
    });
}
const KINDS_THAT_NEED_A_PLACE = new Set([
    "CATALOG_ITEM",
    "EXTERNAL_PLACE",
    "CUSTOM_ACTIVITY",
]);
/**
 * `LOCATION_UNRESOLVED`, `LOCATION_AMBIGUOUS` and `ITEM_ZONE_LOCATION_MISMATCH` for a list of items (contract
 * 3.4). The first two are a WARNING on a draft and BLOCKING on activation for an item that a route check covers
 * (the plan would be judged against a place nobody pinned down). The mismatch is always a WARNING. A cancelled
 * item, and a kind that has no place of its own (transit, a buffer, a request item), raise nothing.
 */
export function locationIssuesFor(items, context) {
    const issues = [];
    for (const item of items) {
        if (item.status === "CANCELLED" || !KINDS_THAT_NEED_A_PLACE.has(item.kind))
            continue;
        if (item.source === null) {
            const code = item.newestDecision === "AMBIGUOUS" ? "LOCATION_AMBIGUOUS" : "LOCATION_UNRESOLVED";
            const blocking = context.phase === "ACTIVATION" && context.routeCheckedItemIds.has(item.itemId);
            issues.push({
                code,
                severity: blocking ? "BLOCKING" : "WARNING",
                sectionId: null,
                itemId: item.itemId,
            });
            continue;
        }
        if (itemZoneMismatchesPlace(item.itemZone, item.suggestedPlaceZone, item.startsAt ?? context.now)) {
            issues.push({
                code: "ITEM_ZONE_LOCATION_MISMATCH",
                severity: "WARNING",
                sectionId: null,
                itemId: item.itemId,
            });
        }
    }
    return Object.freeze(issues);
}
